import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { useCheckout } from "@stripe/react-stripe-js/checkout"
import type { StripeError } from "@stripe/stripe-js"
import { ChevronDown, ChevronUp, Loader2, Tag, X } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useState } from "react"

export function CheckoutSummary() {
  const checkoutState = useCheckout()
  const [showPromoInput, setShowPromoInput] = useState(false)
  const [promoCode, setPromoCode] = useState("")
  const [isApplyingPromo, setIsApplyingPromo] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)

  if (checkoutState.type === "loading") {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-borderDefault bg-surface-foreground p-6">
        <Loader2 className="size-6 animate-spin text-textDefault-caption" />
      </div>
    )
  }

  if (checkoutState.type === "error") {
    return null
  }

  const { checkout } = checkoutState
  const { lineItems, total, discountAmounts, taxAmounts } = checkout

  const appliedDiscount = discountAmounts?.[0]
  const hasAppliedDiscount = discountAmounts && discountAmounts.length > 0

  const handleApplyPromoCode = () => {
    if (!promoCode.trim()) return

    setIsApplyingPromo(true)
    setPromoError(null)

    checkout
      .applyPromotionCode(promoCode.trim())
      .then((result) => {
        if (result.type === "error") {
          setPromoError(result.error.message)
        } else {
          setPromoCode("")
          setShowPromoInput(false)
        }
      })
      .catch((error: StripeError) => {
        if (error.message) {
          setPromoError(error.message)
        } else {
          setPromoError(word("global.error.label"))
        }
      })
      .finally(() => {
        setIsApplyingPromo(false)
      })
  }

  const handleRemovePromoCode = () => {
    setIsApplyingPromo(true)
    setPromoError(null)

    checkout
      .removePromotionCode()
      .then(() => {
        setIsApplyingPromo(false)
        setPromoError(null)
      })
      .catch((error: StripeError) => {
        if (error.message) {
          setPromoError(error.message)
        } else {
          setPromoError(word("global.error.label"))
        }
        setIsApplyingPromo(false)
      })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleApplyPromoCode()
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-surface-foreground p-4">
      {/* Title */}
      <Text fontStyle="TL" className="text-textDefault-heading">
        {word("global.checkout.summary.title")}
      </Text>

      {/* Products list */}
      <div className="flex flex-col gap-3">
        {lineItems.map((item) => (
          <div key={item.id} className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              {/* Image du produit */}
              {item.images[0] && (
                <img src={item.images[0]} alt={item.name} className="size-12 rounded-lg object-cover" />
              )}
              <div className="flex flex-col">
                <Text fontStyle="BM" className="text-textDefault-body">
                  {item.name}
                </Text>
                {item.description && (
                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {item.description}
                  </Text>
                )}
                {item.quantity > 1 && (
                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {word("global.checkout.summary.quantity")}: {item.quantity}
                  </Text>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Text fontStyle="BM" className="whitespace-nowrap text-textDefault-body">
                {item.total.amount}
              </Text>
              {/* Payment frequency */}
              {item.recurring && (
                <Text fontStyle="LS" className="text-textDefault-caption">
                  {formatRecurringInterval(item.recurring.interval, item.recurring.intervalCount)}
                </Text>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Separator */}
      <div className="h-px bg-borderDefault-separator" />

      {/* Promo code */}
      {hasAppliedDiscount && appliedDiscount ? (
        <div className="flex items-center justify-between rounded-xl border border-borderDefault bg-surface-background p-3">
          <div className="flex items-center gap-2">
            <Tag className="size-4 text-primary-500" />
            <span className="text-sm font-medium text-textDefault-body">
              {appliedDiscount.promotionCode ?? appliedDiscount.displayName}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="iconXs"
            onClick={() => void handleRemovePromoCode()}
            disabled={isApplyingPromo}
          >
            {isApplyingPromo ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPromoInput(!showPromoInput)}
            className="justify-start gap-1 px-0 text-textPrimary hover:bg-transparent hover:text-textPrimary-on-secondary-hover"
          >
            {showPromoInput ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            {word("global.checkout.summary.promo.button")}
          </Button>

          <AnimatePresence>
            {showPromoInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Entrez votre code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="md"
                      onClick={handleApplyPromoCode}
                      disabled={isApplyingPromo || !promoCode.trim()}
                    >
                      {isApplyingPromo ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        word("global.checkout.summary.promo.apply")
                      )}
                    </Button>
                  </div>
                  {promoError && <p className="text-sm text-textError">{promoError}</p>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Separator */}
      <div className="h-px bg-borderDefault-separator" />

      {/* Subtotal */}
      <div className="flex flex-col gap-2">
        <SummaryRow label={word("global.checkout.summary.subtotal.title")} value={total.subtotal.amount} />

        {/* Remises */}
        {hasAppliedDiscount &&
          discountAmounts.map((discount, index) => (
            <SummaryRow
              key={index}
              label={discount.promotionCode ?? discount.displayName ?? word("global.checkout.summary.discount.title")}
              value={`-${discount.amount}`}
              valueClassName="text-primary-500"
            />
          ))}

        {/* Taxes */}
        {taxAmounts &&
          taxAmounts.length > 0 &&
          taxAmounts.map((tax, index) => (
            <SummaryRow
              key={index}
              label={tax.displayName || word("global.checkout.summary.tax.title")}
              value={tax.inclusive ? `(${word("global.checkout.summary.tax.included")}) ${tax.amount}` : tax.amount}
              valueClassName="text-textDefault-caption"
            />
          ))}
      </div>

      {/* Séparateur */}
      <div className="h-px bg-borderDefault-separator" />

      {/* Total */}
      <div className="flex items-center justify-between">
        <Text fontStyle="TL" className="text-textDefault-heading">
          {word("global.checkout.summary.total.title")}
        </Text>
        <Text fontStyle="TL" className="text-textDefault-heading">
          {total.total.amount}
        </Text>
      </div>
    </div>
  )
}

type SummaryRowProps = {
  label: string
  value: string
  valueClassName?: string
}

function SummaryRow({ label, value, valueClassName }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between">
      <Text fontStyle="BS" className="text-textDefault-caption">
        {label}
      </Text>
      <Text fontStyle="BS" className={valueClassName ?? "text-textDefault-body"}>
        {value}
      </Text>
    </div>
  )
}

function formatRecurringInterval(interval: string, intervalCount: number): string {
  const intervalLabels: Record<string, string> = {
    day:
      intervalCount === 1
        ? "/ " + word("global.day.label", { count: intervalCount })
        : `/ ${intervalCount} ${word("global.day.label", { count: intervalCount })}`,
    week:
      intervalCount === 1
        ? "/ " + word("global.week.label", { count: intervalCount })
        : `/ ${intervalCount} ${word("global.week.label", { count: intervalCount })}`,
    month:
      intervalCount === 1
        ? "/ " + word("global.month.label", { count: intervalCount })
        : `/ ${intervalCount} ${word("global.month.label", { count: intervalCount })}`,
    year:
      intervalCount === 1
        ? "/ " + word("global.year.label", { count: intervalCount })
        : `/ ${intervalCount} ${word("global.year.label", { count: intervalCount })}`,
  }
  return intervalLabels[interval] || `/ ${intervalCount} ${interval}`
}
